import React, { useMemo } from 'react';
import cx from 'classnames';
import DOMPurify from 'dompurify';
import { showLongTextTipsDialog } from 'lib/components/LongTextTipsDialog';
import { report } from 'lib/utils/dataReport';
import { ATTA_REPORT } from 'lib/constants/atta';
import { AgreementConfig } from 'lib/constants/operator';
import { buildAgreementReportParams } from './agreementReport';
import './AgreementText.scss';

const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'a',
    'span',
    'b',
    'strong',
    'em',
    'i',
    'p',
    'br',
    'img',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'src', 'alt', 'width', 'height', 'colspan', 'rowspan'],
};

const INNER_PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'a',
    'span',
    'b',
    'strong',
    'em',
    'i',
    'p',
    'br',
    'ul',
    'ol',
    'li',
    'h2',
    'h3',
    'h4',
    'img',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'src', 'alt', 'width', 'height', 'colspan', 'rowspan'],
};

function getAgreementLinkHash(rawHref: string): string {
  try {
    return new URL(rawHref, window.location.href).hash;
  } catch {
    return rawHref.startsWith('#') ? rawHref : '';
  }
}

interface AgreementTextProps {
  config: AgreementConfig;
  packageId?: string;
  /**
   * 预览态：来自 SMSAgreementConfig "所见即所得"调试组件，所有 EVT-23/25/26 上报均关闭，
   * 但协议文本弹窗、外链跳转等交互保留，便于走查。
   */
  previewMode?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * 协议文案渲染组件
 * - 渲染 config.displayText 富文本
 * - 事件委托拦截 <a> 点击：
 *     href="#agreement-{id}" → 上报 EVT-23（agreement_link_click）+ EVT-25（agreement_modal_view），
 *                              并通过 LongTextTipsDialog 的 onConfirm/onClose 回调上报 EVT-26（agreement_modal_close）
 *     href="https://..."      → 上报 EVT-23（kind=external），window.open 外链
 *     其它/空                 → 上报 EVT-23（kind=invalid），阻止默认跳转
 * - 被 AgreementCheckbox（订购弹窗勾选区）和二次确认弹窗共同复用
 */
const AgreementText: React.FC<AgreementTextProps> = ({ config, packageId, previewMode = false, className, style }) => {
  const safeHtml = useMemo(() => DOMPurify.sanitize(config.displayText || '', PURIFY_CONFIG), [config.displayText]);

  const reportSafe = (key: string, params: Record<string, unknown>) => {
    if (previewMode) return;
    report(key, { extraStatisticParams: params });
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest('a');
    if (!anchor) return;

    e.preventDefault();
    e.stopPropagation();

    const rawHref = anchor.getAttribute('href') || '';

    // 富文本编辑器有时会把 #agreement-xxx 转成完整 URL（如 https://page.com/path#agreement-yzf）
    // 统一提取 hash 部分再做判断，兼容两种格式
    const hash = getAgreementLinkHash(rawHref);

    const reportContext = { packageId, agreementVersion: config.agreementVersion };

    if (hash.startsWith('#agreement-')) {
      const id = hash.slice('#agreement-'.length);
      const item = (config.agreements || []).find((a) => a.id === id);
      if (!item) return;

      // EVT-23 协议链接点击（站内）
      reportSafe(
        ATTA_REPORT.VERIFY.AGREEMENT_LINK_CLICK,
        buildAgreementReportParams(reportContext, {
          agreementId: item.id,
          title: item.title,
          kind: 'inline',
        }),
      );

      // EVT-25 协议文本弹窗曝光
      reportSafe(
        ATTA_REPORT.VERIFY.AGREEMENT_MODAL_VIEW,
        buildAgreementReportParams(reportContext, { agreementId: item.id }),
      );

      const safeContent = DOMPurify.sanitize(item.content || '', INNER_PURIFY_CONFIG);
      // EVT-26 closeType：confirm = 点"我知道了"；close = 点 × / ESC
      let closeReported = false;
      const reportClose = (closeType: 'confirm' | 'close') => {
        if (closeReported) return;
        closeReported = true;
        reportSafe(
          ATTA_REPORT.VERIFY.AGREEMENT_MODAL_CLOSE,
          buildAgreementReportParams(reportContext, { agreementId: item.id, closeType }),
        );
      };
      showLongTextTipsDialog({
        title: `《${item.title}》`,
        content: (
          <div
            className="yzf-agreement-dialog__text"
            style={{ marginTop: '0.2rem' }}
            dangerouslySetInnerHTML={{ __html: safeContent }}
          />
        ),
        buttonText: '关闭',
        onConfirm: () => reportClose('confirm'),
        onClose: () => reportClose('close'),
      });
      return;
    }

    if (/^https?:\/\//i.test(rawHref)) {
      reportSafe(
        ATTA_REPORT.VERIFY.AGREEMENT_LINK_CLICK,
        buildAgreementReportParams(reportContext, {
          agreementId: '',
          title: anchor.textContent || '',
          kind: 'external',
        }),
      );
      window.location.href = rawHref;
      return;
    }

    reportSafe(
      ATTA_REPORT.VERIFY.AGREEMENT_LINK_CLICK,
      buildAgreementReportParams(reportContext, {
        agreementId: '',
        title: anchor.textContent || '',
        kind: 'invalid',
      }),
    );
  };

  return (
    <div
      className={cx('yzf-agreement-text', className)}
      style={style}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
      onClick={handleClick}
    />
  );
};

export default AgreementText;
